// Injectable WebRTC surface + per-connection plumbing. The engine talks to
// these minimal structural interfaces (a browser RTCPeerConnection satisfies
// them) so the whole call path is testable with mock peers, and the Android
// WebView needs no shims.

import type { IceCandidate, IceServer } from '@merkury/protocol';
import type { TimerHost } from '../types.js';
import type { StatsEntry } from '../quality/monitor.js';

/** Trickle-ICE batching window (docs/02 §4: batched ≤50 ms). */
export const CANDIDATE_BATCH_MS = 50;

export interface SessionDescriptionLike {
  type: string;
  sdp: string;
}

export interface AudioTrackLike {
  enabled: boolean;
  stop?(): void;
}

export interface MediaStreamLike {
  getAudioTracks(): AudioTrackLike[];
}

export interface RtpSenderLike {
  getParameters?(): { encodings?: Array<Record<string, unknown>> };
  setParameters?(p: { encodings?: Array<Record<string, unknown>> }): Promise<void> | void;
}

export interface RtpReceiverLike {
  jitterBufferTarget?: number | null;
}

/** Subset of RTCTrackEvent the engine consumes. */
export interface TrackEventLike {
  track?: AudioTrackLike;
  streams?: readonly MediaStreamLike[];
}

export interface PeerConnectionLike {
  readonly signalingState: string;
  readonly iceConnectionState: string;
  readonly localDescription: SessionDescriptionLike | null;
  onicecandidate: ((ev: { candidate: IceCandidate | null }) => void) | null;
  oniceconnectionstatechange: (() => void) | null;
  /** Remote media arriving (initial connect and renegotiations). */
  ontrack?: ((ev: TrackEventLike) => void) | null;
  createOffer(opts?: { iceRestart?: boolean }): Promise<SessionDescriptionLike>;
  createAnswer(): Promise<SessionDescriptionLike>;
  setLocalDescription(desc: SessionDescriptionLike): Promise<void>;
  setRemoteDescription(desc: SessionDescriptionLike): Promise<void>;
  addIceCandidate(candidate: IceCandidate): Promise<void>;
  restartIce?(): void;
  setConfiguration?(config: PeerConfig): void;
  close(): void;
  getStats?(): Promise<Iterable<StatsEntry>>;
  addTrack?(track: AudioTrackLike, stream: MediaStreamLike): unknown;
  getSenders?(): RtpSenderLike[];
  getReceivers?(): RtpReceiverLike[];
}

export interface PeerConfig {
  iceServers: IceServer[];
  /** Rung 4 forces `relay` (TURN/TCP/TLS) — docs/01 §5. */
  iceTransportPolicy: 'all' | 'relay';
}

export type PeerConnectionFactory = (config: PeerConfig) => PeerConnectionLike;

/** Host media access (getUserMedia etc.), injected by J7/J9 wiring. */
export interface MediaAdapter {
  /** Mic capture with echoCancellation/noiseSuppression/autoGainControl on. */
  getLocalAudio(): Promise<MediaStreamLike>;
}

// ---------------------------------------------------------------------------
// SDP type marker for `call.renegotiate`
// ---------------------------------------------------------------------------
// The wire payload carries only an opaque `sdp` string; renegotiation needs to
// distinguish offer from answer. Both ends are this codebase, so we append a
// standard "unknown attribute" line (ignored by SDP parsers, outside the DTLS
// fingerprint) naming the description type.

export const SDP_TYPE_ATTR = 'a=x-merkury-sdptype:';

export function withSdpTypeMarker(sdp: string, type: 'offer' | 'answer'): string {
  const sep = sdp.endsWith('\n') ? '' : '\r\n';
  return `${sdp}${sep}${SDP_TYPE_ATTR}${type}\r\n`;
}

export function sdpTypeFromMarker(sdp: string): 'offer' | 'answer' | null {
  const m = /^a=x-merkury-sdptype:(offer|answer)\s*$/im.exec(sdp);
  return m ? (m[1] as 'offer' | 'answer') : null;
}

// ---------------------------------------------------------------------------
// PeerHandle: candidate batching + ICE state fan-out for one connection
// ---------------------------------------------------------------------------

export interface PeerHandleEvents {
  /** A ≤50 ms batch of local trickle-ICE candidates ready to send. */
  onCandidateBatch(candidates: IceCandidate[]): void;
  onIceState(state: string): void;
  /** Remote audio arrived (initial connect or after a renegotiation). */
  onRemoteStream(stream: MediaStreamLike): void;
}

export class PeerHandle {
  private batch: IceCandidate[] = [];
  private flushTimer: unknown = null;
  private closed = false;

  constructor(
    readonly pc: PeerConnectionLike,
    private readonly timers: TimerHost,
    private readonly events: PeerHandleEvents,
  ) {
    pc.onicecandidate = (ev) => {
      if (this.closed) return;
      if (ev.candidate && ev.candidate.candidate !== '') {
        this.batch.push(ev.candidate);
        if (this.flushTimer === null) {
          this.flushTimer = this.timers.setTimeout(() => this.flush(), CANDIDATE_BATCH_MS);
        }
      } else {
        this.flush(); // end-of-candidates: push out whatever is pending
      }
    };
    pc.oniceconnectionstatechange = () => {
      if (!this.closed) this.events.onIceState(pc.iceConnectionState);
    };
    pc.ontrack = (ev) => {
      if (this.closed) return;
      // Prefer the browser-supplied stream; wrap a bare track when absent.
      const stream =
        ev.streams?.[0] ?? (ev.track ? { getAudioTracks: () => [ev.track!] } : null);
      if (stream) this.events.onRemoteStream(stream);
    };
  }

  flush(): void {
    if (this.flushTimer !== null) {
      this.timers.clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.batch.length > 0) {
      const out = this.batch;
      this.batch = [];
      this.events.onCandidateBatch(out);
    }
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    if (this.flushTimer !== null) {
      this.timers.clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    this.pc.onicecandidate = null;
    this.pc.oniceconnectionstatechange = null;
    this.pc.ontrack = null;
    try {
      this.pc.close();
    } catch {
      // already closed
    }
  }
}
